import { INoteMutation, INotePhoto, spLog } from "@eventr/common";
import axios from "axios";
import { StateCreator } from "zustand";
import { DataAPI } from "../data/Data";
import { useConnectivityStore } from "../feat/connectivity/connectivity.store";
import { noteOutbox } from "../feat/notes/note-outbox";
import {
  applyNoteChanges,
  IAppNote,
  INoteAuthor,
  INoteChange,
  isLocalPhoto,
  reconcileNotes,
  TNotePhoto,
} from "../feat/notes/note-state";
import { BoundStore } from "./bound.store";

interface NotesState {
  /** Keyed by itinerary id. */
  notesByItinerary: Record<string, IAppNote[]>;
  /** Titles of notes a sync found removed by a moderator or with their item. Shown, then cleared. */
  removedNoteTitles: string[];
}

interface NotesActions {
  /** Fetches an itinerary's notes, then lays anything still queued over them. */
  loadNotes: (itineraryId: number) => Promise<void>;
  /** Applies the change locally, then queues it. Safe with no connection. */
  saveNote: (itineraryId: number, change: INoteChange) => Promise<void>;
  /** Keeps a photo on the device until it uploads, and returns its placeholder. */
  storeNotePhoto: (blob: Blob) => Promise<TNotePhoto>;
  /** Uploads queued photos, replays queued changes and folds the server's answer back in. */
  syncNotes: () => Promise<void>;
  clearRemovedNoteTitles: () => void;
  clearNotes: () => void;
}

export interface NotesSlice extends NotesState, NotesActions {}

const author = (s: BoundStore): INoteAuthor => ({
  id: s.wpUser?.id ?? 0,
  name: [s.wpUser?.firstName, s.wpUser?.lastName].filter(Boolean).join(" "),
});

/** The server refused these for good: the note is gone, so the device drops it and says so. */
const REMOVED_REASONS = new Set(["deleted", "item-missing"]);

export const createNotesSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], NotesSlice> = (set, get) => {
  /** Swaps uploaded photos into the note on screen, unless it has been edited since. */
  const swapPlaceholders = (uploaded: INoteChange) =>
    set(
      (s) => {
        const itineraryId = Object.keys(s.notesByItinerary).find((id) =>
          s.notesByItinerary[id]?.some((n) => n.id === uploaded.id),
        );
        if (!itineraryId) return s;
        return {
          notesByItinerary: {
            ...s.notesByItinerary,
            [itineraryId]: (s.notesByItinerary[itineraryId] ?? []).map((n) =>
              n.id === uploaded.id && n.modifiedAt === uploaded.modifiedAt ? { ...n, photos: uploaded.photos } : n,
            ),
          },
        };
      },
      false,
      "swapNotePhotoPlaceholders",
    );

  return {
    notesByItinerary: {},
    removedNoteTitles: [],

    loadNotes: async (itineraryId) => {
      if (!useConnectivityStore.getState().isConnectivityAvailable) return;

      try {
        const org = get().selectedOrg?.name ?? "";
        const server = await DataAPI.notes.get(itineraryId);
        const queued = (await noteOutbox.pending(org, itineraryId)).map((q) => q.change);
        set(
          (s) => ({
            notesByItinerary: {
              ...s.notesByItinerary,
              [itineraryId]: reconcileNotes(server, queued, itineraryId, author(s)),
            },
          }),
          false,
          "loadNotes",
        );
      } catch (error) {
        // The persisted copy stands in until the next attempt.
        spLog("Notes load deferred", error, ["NOTES"]);
      }
    },

    saveNote: async (itineraryId, change) => {
      set(
        (s) => ({
          notesByItinerary: {
            ...s.notesByItinerary,
            [itineraryId]: applyNoteChanges(s.notesByItinerary[itineraryId] ?? [], [change], itineraryId, author(s)),
          },
        }),
        false,
        "saveNote",
      );

      await noteOutbox.queue(get().selectedOrg?.name ?? "", itineraryId, change);
      await get().syncNotes();
    },

    storeNotePhoto: async (blob) => {
      const localId = crypto.randomUUID();
      await noteOutbox.savePhoto(localId, blob);
      return { localId };
    },

    syncNotes: async () => {
      // guestAbortRequestInterceptor cancels every request while offline.
      if (!useConnectivityStore.getState().isConnectivityAvailable) return;

      const org = get().selectedOrg?.name ?? "";
      const queued = await noteOutbox.pending(org);
      const itineraryIds = [...new Set(queued.map((q) => q.itineraryId))];

      for (const itineraryId of itineraryIds) {
        try {
          const changes = await Promise.all(
            queued
              .filter((q) => q.itineraryId === itineraryId)
              .map((q) => uploadPhotos(get, org, q.itineraryId, q.change, swapPlaceholders)),
          );
          const mutations: INoteMutation[] = changes.map((change) => ({
            ...change,
            photos: change.photos.filter((photo): photo is INotePhoto => !isLocalPhoto(photo)),
          }));

          const response = await DataAPI.notes.sync(itineraryId, mutations);
          await noteOutbox.settle(org, changes);

          const removed: string[] = [];
          for (const rejection of response.rejected) {
            await noteOutbox.discard(org, rejection.id);
            if (REMOVED_REASONS.has(rejection.reason)) {
              const note = get().notesByItinerary[itineraryId]?.find((n) => n.id === rejection.id);
              removed.push(note?.title ?? "");
            }
          }

          // Anything queued while the request was in flight has to survive the answer.
          const stillQueued = (await noteOutbox.pending(org, itineraryId)).map((q) => q.change);
          set(
            (s) => ({
              notesByItinerary: {
                ...s.notesByItinerary,
                [itineraryId]: reconcileNotes(response, stillQueued, itineraryId, author(s)),
              },
              removedNoteTitles: removed.length > 0 ? [...s.removedNoteTitles, ...removed] : s.removedNoteTitles,
            }),
            false,
            "syncNotes",
          );
        } catch (error) {
          // Silent by design. The change is already on screen and stays queued.
          spLog("Note sync deferred", error, ["NOTES"]);
          if (!axios.isCancel(error)) {
            await noteOutbox.recordFailure(org);
          }
        }
      }
    },

    clearRemovedNoteTitles: () => set({ removedNoteTitles: [] }, false, "clearRemovedNoteTitles"),

    clearNotes: () => {
      set({ notesByItinerary: {}, removedNoteTitles: [] }, false, "clearNotes");
      void noteOutbox.clear();
    },
  };
};

/**
 * Uploads a change's photos that are still on the device, and writes the uploaded copy back
 * to the queue and the store, so a later failure does not upload them again.
 */
const uploadPhotos = async (
  get: () => BoundStore,
  org: string,
  itineraryId: number,
  change: INoteChange,
  onUploaded: (uploaded: INoteChange) => void,
): Promise<INoteChange> => {
  if (!change.photos.some(isLocalPhoto)) return change;

  const photos: TNotePhoto[] = [];
  for (const photo of change.photos) {
    if (!isLocalPhoto(photo)) {
      photos.push(photo);
      continue;
    }
    const alreadyUploaded = await noteOutbox.getUploaded(photo.localId);
    if (alreadyUploaded) {
      photos.push(alreadyUploaded);
      continue;
    }
    const blob = await noteOutbox.getPhoto(photo.localId);
    if (!blob) continue;

    const response = await get().uploadMedia(
      new File([blob], `${photo.localId}.jpeg`, { type: blob.type }),
      "Note",
      org,
    );
    const media = response.data as { id?: number; source_url?: string } | undefined;
    if (response.status !== "success" || !media?.id || !media.source_url) {
      throw new Error("Note photo upload failed");
    }
    const uploaded = { mediaId: media.id, url: media.source_url };
    await noteOutbox.photoUploaded(photo.localId, uploaded);
    photos.push(uploaded);
  }

  const uploaded = { ...change, photos };
  await noteOutbox.replaceIfUnchanged(org, itineraryId, change, uploaded);
  onUploaded(uploaded);
  return uploaded;
};
